import { Box, Link, Stack, Typography } from '@mui/material';
import { usePaymentData } from 'api/payment/payment.queries';
import { useSubmission } from 'api/submission';
import { META, STATES } from 'common/constants';
import { FeedbackWrapper } from 'components/FeedbackWrapper';
import { currencyFormat } from 'helpers/utils';
import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

export const ENROLLMENT_INFO_MESSAGE =
  'Autopay enrollment could not be completed. Try again after paying all outstanding invoices.';

export const GENERAL_INFO_MESSAGE =
  'Could not complete autopay enrollment. Please, contact your support team.';

const PurchasedInfo = () => {
  const [searchParams] = useSearchParams();

  const { id: submissionId } = useParams() as { id: string };

  const { data: submission } = useSubmission(submissionId);

  const premiumAmount =
    submission?.data.paymentScheduleName === 'Monthly'
      ? submission?.price
      : submission?.discounted_price;

  const policyTerms = submission?.policyTerms || '';

  const { data: paymentData } = usePaymentData(submissionId as string, {
    retry: submission?.us_state === STATES.MI ? 3 : 1,
  });

  const status = searchParams.get('status');

  const enrollmentFailed = searchParams.get('enrollment_failed');

  const successFullPurchaseInfoData = useMemo(
    () => [
      {
        title: 'Named Insured',
        value: submission?.data?.uw_questions?.businessName || '',
      },
      {
        title: 'Policy Number',
        value: submission?.data?.policy_data?.pol_policy_number || '',
      },
      {
        title: 'Policy Term',
        value: policyTerms,
      },
      {
        title: 'Premium Amount',
        value: currencyFormat(premiumAmount || 0).merged,
      },
    ],
    [submission, paymentData],
  );

  return (
    <Box>
      <FeedbackWrapper
        feedback="referred"
        renderAdditionalInfo={false}
        homeButtonText="Go to My THREE Online"
        homeButtonHref={META.MYTHREE_ONLINE_LINK}
        sx={{
          mx: 3,
        }}
      >
        <Stack gap={3}>
          <Typography variant="h1">Success!</Typography>

          <Stack gap={2}>
            <Typography variant="body1">
              Your payment has been processed successfully. Your policy will be mailed to you, as
              you’ve requested.
            </Typography>

            <Box>
              <Typography variant="body1" display="inline">
                Your payment can be viewed in My THREE Online and a confirmation email of your
                processed payment will also be sent to:
              </Typography>
              <Link
                variant="h3"
                sx={{ ml: 0.5, textTransform: 'none', textDecoration: 'none' }}
                href={`mailto:${paymentData?.customer_email}`}
                target="_blank"
              >
                {paymentData?.customer_email}
              </Link>
            </Box>
          </Stack>

          {enrollmentFailed === 'true' && (
            <Typography
              sx={{
                color: (theme) => theme.palette.customColors.darkGray,
                p: 2,
                backgroundColor: (theme) => theme.palette.customColors.lightGray,
                borderRadius: '0.5rem',
              }}
            >
              {status === '400' ? ENROLLMENT_INFO_MESSAGE : GENERAL_INFO_MESSAGE}
            </Typography>
          )}

          <Stack
            gap={1}
            sx={{
              border: (theme) => `1px solid ${theme.palette.customColors.lightGray}`,
              borderRadius: (theme) => theme.spacing(1),
              padding: (theme) => theme.spacing(3),
            }}
          >
            {successFullPurchaseInfoData.map(({ title, value }) => (
              <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} key={title}>
                <Typography
                  display="inline"
                  variant="body1"
                  minWidth={144}
                  sx={{
                    color: (theme) => theme.palette.customColors.darkGray,
                  }}
                >
                  {title}
                </Typography>

                <Typography display="inline" variant="h3">
                  {value}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </FeedbackWrapper>
    </Box>
  );
};

export default PurchasedInfo;
